import React, { useState, useEffect } from 'react';
import { TrendingUp, Hash, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { HashtagChip } from './HashtagChip';
import { getTrendingHashtags } from '../../lib/discoveryService';

interface TrendingHashtagsProps {
  limit?: number;
  showTitle?: boolean;
  variant?: 'card' | 'inline';
  className?: string;
}

export const TrendingHashtags: React.FC<TrendingHashtagsProps> = ({
  limit = 10,
  showTitle = true,
  variant = 'card',
  className = ''
}) => {
  const [hashtags, setHashtags] = useState<{ hashtag: string; count: number }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTrendingHashtags();
  }, [limit]);

  const loadTrendingHashtags = async () => {
    try {
      setLoading(true);
      const trending = await getTrendingHashtags(limit);
      setHashtags(trending);
    } catch (error) {
      console.error('Error loading trending hashtags:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`${variant === 'card' ? 'bg-white border border-gray-200 rounded-md p-4' : ''} ${className}`}>
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
        </div>
      </div>
    );
  }

  if (hashtags.length === 0) {
    return null;
  }

  const content = (
    <>
      {showTitle && (
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <TrendingUp className="h-5 w-5 mr-2 text-purple-600" />
            Trending Hashtags
          </h3>
          <Link 
            to="/explore?tab=trending" 
            className="text-sm text-purple-600 hover:text-purple-700 flex items-center"
          >
            See all
            <ChevronRight className="h-4 w-4 ml-1" />
          </Link>
        </div>
      )}

      <div className="space-y-3">
        {hashtags.map((item, index) => (
          <Link
            key={index}
            to={`/hashtag/${item.hashtag}`}
            className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors group"
          >
            <div className="flex items-center">
              <div className="flex items-center justify-center w-8 h-8 bg-purple-100 text-purple-600 rounded-full mr-3 text-sm font-bold group-hover:bg-purple-200 transition-colors">
                {index + 1}
              </div>
              <div>
                <div className="flex items-center">
                  <Hash className="h-4 w-4 text-gray-400 mr-1" />
                  <span className="font-medium text-gray-900">{item.hashtag}</span>
                </div>
                <p className="text-sm text-gray-500">{item.count} posts</p>
              </div>
            </div>
            <TrendingUp className="h-4 w-4 text-gray-400 group-hover:text-purple-600 transition-colors" />
          </Link>
        ))}
      </div>
    </>
  );

  if (variant === 'inline') {
    return <div className={className}>{content}</div>;
  }

  return (
    <div className={`bg-white border border-gray-200 rounded-md overflow-hidden ${className}`}>
      <div className="p-4">
        {content}
      </div>
    </div>
  );
};